import React, { useState } from 'react';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Select from '../../components/ui/Select';
import MetricCard from './components/MetricCard';
import ViewTrendsChart from './components/ViewTrendsChart';
import PopularPagesHeatmap from './components/PopularPagesHeatmap';
import HotspotClicksChart from './components/HotspotClicksChart';
import EngagementTable from './components/EngagementTable';
import FilterControls from './components/FilterControls';
import RealTimeVisitors from './components/RealTimeVisitors';
import GA4IntegrationStatus from './components/GA4IntegrationStatus';

const AnalyticsDashboard = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedCatalog, setSelectedCatalog] = useState('all');
  const [dateRange, setDateRange] = useState('last30days');
  const [deviceType, setDeviceType] = useState('all');
  const [geography, setGeography] = useState('all');

  const catalogOptions = [
    { value: 'all', label: 'All Catalogs (Company-wide)' },
    { value: 'cat1', label: 'Spring Collection 2026' },
    { value: 'cat2', label: 'Product Catalog Q1' },
    { value: 'cat3', label: 'Holiday Gift Guide' },
    { value: 'cat4', label: 'Technical Specifications' }
  ];

  const metricsData = [
    {
      title: 'Total Views',
      value: '45,892',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'Eye',
      trend: 'Steady growth'
    },
    {
      title: 'Unique Visitors',
      value: '28,456',
      change: '+8.3%',
      changeType: 'positive',
      icon: 'Users',
      trend: 'Increasing'
    },
    {
      title: 'Avg. Session Duration',
      value: '4m 32s',
      change: '+15.2%',
      changeType: 'positive',
      icon: 'Clock',
      trend: 'Strong engagement'
    },
    {
      title: 'Conversion Rate',
      value: '3.8%',
      change: '-2.1%',
      changeType: 'negative',
      icon: 'TrendingUp',
      trend: 'Needs attention'
    }
  ];

  const viewTrendsData = [
    { date: '01/01', views: 1245, visitors: 892 },
    { date: '01/05', views: 1567, visitors: 1123 },
    { date: '01/10', views: 1823, visitors: 1345 },
    { date: '01/15', views: 2134, visitors: 1567 },
    { date: '01/20', views: 1956, visitors: 1432 },
    { date: '01/25', views: 2345, visitors: 1678 },
    { date: '01/30', views: 2567, visitors: 1834 }
  ];

  const popularPagesData = Array.from({ length: 40 }, (_, i) => ({
    pageNumber: i + 1,
    views: Math.floor(Math.random() * 5000) + 500,
    thumbnail: `https://images.unsplash.com/photo-${1500000000000 + i * 1000000}?w=200&h=280&fit=crop`
  }));

  const hotspotClicksData = [
    { name: 'Buy Now - Product A', clicks: 2345, ctr: 8.5 },
    { name: 'Learn More - Feature B', clicks: 1876, ctr: 6.8 },
    { name: 'External Link - Website', clicks: 1654, ctr: 6.2 },
    { name: 'Buy Now - Product C', clicks: 1432, ctr: 5.4 },
    { name: 'Video Play - Demo', clicks: 1234, ctr: 4.9 }
  ];

  const topPagesColumns = [
    { key: 'page', label: 'Page', sortable: true },
    { 
      key: 'views', 
      label: 'Views', 
      sortable: true,
      render: (value) => <span className="font-semibold">{value?.toLocaleString()}</span>
    },
    { 
      key: 'avgTime', 
      label: 'Avg. Time', 
      sortable: true,
      render: (value) => <span className="text-muted-foreground">{value}</span>
    },
    { 
      key: 'bounceRate', 
      label: 'Bounce Rate', 
      sortable: true,
      render: (value) => (
        <span className={`font-medium ${value > 50 ? 'text-destructive' : 'text-success'}`}>
          {value}%
        </span>
      )
    }
  ];

  const topPagesData = [
    { page: 'Page 1 - Cover', views: 5234, avgTime: '2m 45s', bounceRate: 23 },
    { page: 'Page 5 - Featured Products', views: 4567, avgTime: '3m 12s', bounceRate: 18 },
    { page: 'Page 12 - Special Offers', views: 3892, avgTime: '2m 56s', bounceRate: 25 },
    { page: 'Page 8 - New Arrivals', views: 3456, avgTime: '2m 34s', bounceRate: 31 },
    { page: 'Page 15 - Customer Reviews', views: 3234, avgTime: '4m 18s', bounceRate: 15 },
    { page: 'Page 3 - About Us', views: 2987, avgTime: '1m 52s', bounceRate: 42 },
    { page: 'Page 20 - Contact Info', views: 2765, avgTime: '1m 23s', bounceRate: 56 },
    { page: 'Page 10 - Product Details', views: 2543, avgTime: '3m 45s', bounceRate: 22 },
    { page: 'Page 7 - Testimonials', views: 2345, avgTime: '2m 18s', bounceRate: 28 },
    { page: 'Page 18 - FAQ', views: 2123, avgTime: '3m 02s', bounceRate: 35 }
  ];

  const topHotspotsColumns = [
    { key: 'hotspot', label: 'Hotspot', sortable: true },
    { key: 'type', label: 'Type', sortable: true },
    { 
      key: 'clicks', 
      label: 'Clicks', 
      sortable: true,
      render: (value) => <span className="font-semibold">{value?.toLocaleString()}</span>
    },
    { 
      key: 'ctr', 
      label: 'CTR', 
      sortable: true,
      render: (value) => (
        <span className={`font-medium ${value > 5 ? 'text-success' : 'text-warning'}`}>
          {value}%
        </span>
      )
    }
  ];

  const topHotspotsData = [
    { hotspot: 'Buy Now - Premium Package', type: 'Buy Button', clicks: 2345, ctr: 8.5 },
    { hotspot: 'Learn More - Features', type: 'External Link', clicks: 1876, ctr: 6.8 },
    { hotspot: 'Shop Collection', type: 'Button', clicks: 1654, ctr: 6.2 },
    { hotspot: 'Watch Demo Video', type: 'External Link', clicks: 1432, ctr: 5.4 },
    { hotspot: 'Add to Cart - Product A', type: 'Buy Button', clicks: 1234, ctr: 4.9 },
    { hotspot: 'Download Brochure', type: 'Button', clicks: 1123, ctr: 4.5 },
    { hotspot: 'Contact Sales', type: 'External Link', clicks: 987, ctr: 3.8 },
    { hotspot: 'View Specifications', type: 'Button', clicks: 876, ctr: 3.4 },
    { hotspot: 'Request Quote', type: 'External Link', clicks: 765, ctr: 3.1 },
    { hotspot: 'Compare Products', type: 'Button', clicks: 654, ctr: 2.7 }
  ];

  const trafficSourcesColumns = [
    { key: 'source', label: 'Source', sortable: true },
    { 
      key: 'visitors', 
      label: 'Visitors', 
      sortable: true,
      render: (value) => <span className="font-semibold">{value?.toLocaleString()}</span>
    },
    { 
      key: 'percentage', 
      label: 'Percentage', 
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
            <div 
              className="bg-accent h-full rounded-full transition-all duration-250"
              style={{ width: `${value}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-foreground w-12 text-right">{value}%</span>
        </div>
      )
    }
  ];

  const trafficSourcesData = [
    { source: 'Direct Traffic', visitors: 12456, percentage: 43.8 },
    { source: 'Organic Search', visitors: 8234, percentage: 28.9 },
    { source: 'Social Media', visitors: 4567, percentage: 16.1 },
    { source: 'Email Campaigns', visitors: 2345, percentage: 8.2 },
    { source: 'Referral Links', visitors: 854, percentage: 3.0 }
  ];

  const handleExport = () => {
    console.log('Exporting analytics data...');
  };

  const handleResetFilters = () => {
    setDateRange('last30days');
    setDeviceType('all');
    setGeography('all');
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <main className={`flex-1 transition-all duration-250 ${sidebarCollapsed ? 'lg:ml-20' : 'lg:ml-[280px]'}`}>
        <div className="p-4 md:p-6 lg:p-8 pt-16 lg:pt-8">
          <div className="mb-6 md:mb-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
              <div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-foreground mb-2">Analytics Dashboard</h1>
                <p className="text-sm md:text-base text-muted-foreground">Comprehensive insights into catalog performance and user engagement</p>
              </div>
              <div className="flex items-center gap-2">
                <Icon name="BarChart3" size={20} className="text-accent" />
                <span className="text-sm text-muted-foreground">Last updated: Just now</span>
              </div>
            </div>
            
            <div className="max-w-md">
              <Select
                label="Select Catalog"
                options={catalogOptions}
                value={selectedCatalog}
                onChange={setSelectedCatalog}
              />
            </div>
          </div>

          <div className="space-y-6 md:space-y-8">
            <FilterControls
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              deviceType={deviceType}
              onDeviceTypeChange={setDeviceType}
              geography={geography}
              onGeographyChange={setGeography}
              onExport={handleExport}
              onReset={handleResetFilters}
            />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
              {metricsData?.map((metric, index) => (
                <MetricCard key={index} {...metric} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="lg:col-span-2">
                <ViewTrendsChart data={viewTrendsData} chartType="area" />
              </div>
              <div className="space-y-6">
                <RealTimeVisitors initialCount={47} />
                <GA4IntegrationStatus 
                  isConnected={true} 
                  lastSyncTime="2 minutes ago" 
                  customEvents={5}
                />
              </div>
            </div>

            <PopularPagesHeatmap pages={popularPagesData} />

            <HotspotClicksChart data={hotspotClicksData} />

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              <EngagementTable 
                data={topPagesData}
                title="Top Performing Pages"
                columns={topPagesColumns}
                type="pages"
              />
              <EngagementTable 
                data={topHotspotsData}
                title="Most Clicked Hotspots"
                columns={topHotspotsColumns}
                type="hotspots"
              />
            </div>

            <EngagementTable 
              data={trafficSourcesData}
              title="Traffic Sources"
              columns={trafficSourcesColumns}
              type="sources"
            />
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnalyticsDashboard;